/* MyBoost app shell — ES module. Pure helpers are exported for node tests. */
const SHEET_ATTR = 'data-mba-sheet';
const REF_COOKIE = 'mb_ref';
const REF_SYNC_KEY = 'mba_ref_synced';
const REF_CLICK_KEY = 'mba_ref_clicked';
const PLATFORM_HOSTS = {
  instagram: ['instagram.com'],
  tiktok: ['tiktok.com'],
  youtube: ['youtube.com', 'youtu.be'],
  facebook: ['facebook.com', 'fb.com', 'fb.watch'],
  spotify: ['spotify.com'],
  x: ['x.com', 'twitter.com'],
  twitch: ['twitch.tv'],
};

export function normalizeRef(value) {
  if (typeof value !== 'string') return null;
  const ref = value.trim().toUpperCase();
  return /^[A-Z0-9_-]{4,32}$/.test(ref) ? ref : null;
}

export function readCookie(cookieString, name) {
  const entry = String(cookieString || '').split(';').map((part) => part.trim()).find((part) => part.startsWith(`${name}=`));
  if (!entry) return null;
  const raw = entry.slice(name.length + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Le lien d'un affilié peut porter ?ref= ou ?mb_ref= : les deux valent. */
export function refFromSearch(search) {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return null;
  }
  return normalizeRef(params.get('ref')) || normalizeRef(params.get('mb_ref'));
}

const AUTH_PARAM = /^(token|token_hash|type|code|state|access_token|refresh_token|id_token|provider_token|expires_at|expires_in|token_type|error|error_code|error_description)$/i;

/** La page d'arrivée telle qu'on peut la rapporter : sans fragment, sans jeton ni paramètre
    d'authentification, et jamais l'écran de retour du lien magique du Club. */
function landingUrl(href) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  if (/^(\/[a-z]{2}(-[a-z]{2,4})?)?\/pages\/club-auth\/?$/i.test(url.pathname)) return undefined;
  for (const key of [...url.searchParams.keys()]) if (AUTH_PARAM.test(key)) url.searchParams.delete(key);
  url.hash = '';
  return url.href.slice(0, 400);
}

/** Ce que l'on rapporte d'un clic : le code, la page d'arrivée, et l'origine si elle est connue.
    Aucune donnée personnelle, aucun identifiant : l'endpoint hache l'IP de son côté. */
export function clickPayload(href, search) {
  const ref = refFromSearch(search);
  if (!ref) return null;
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    params = new URLSearchParams('');
  }
  const text = (value) => (typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, 120) : undefined);
  const payload = { ref_code: ref };
  const landing = landingUrl(href);
  if (landing) payload.landing_url = landing;
  const source = text(params.get('utm_source'));
  if (source) payload.source = source;
  const campaign = text(params.get('utm_campaign'));
  if (campaign) payload.campaign = campaign;
  const medium = text(params.get('utm_medium'));
  if (medium) payload.medium = medium;
  return payload;
}

export function refSyncKey(ref, cartToken) {
  return ref && cartToken ? `${ref}:${cartToken}` : null;
}

export function formatPrice(cents, locale, currency) {
  if (cents === null || cents === undefined || cents === '') return '';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n / 100);
  } catch {
    return `${(n / 100).toFixed(2)} ${currency || ''}`.trim();
  }
}

function isDomainLookingHandle(handle) {
  if (!/\.[a-z]{2,}$/i.test(handle)) return false;
  return Object.values(PLATFORM_HOSTS).some((hosts) =>
    hosts.some((host) => handle === host || handle.endsWith(`.${host}`)));
}

const HOST_ALIASES = {
  'x.com': 'twitter.com',
  'fb.com': 'facebook.com',
  'fb.watch': 'facebook.com',
};

// Rewrites share-link hosts (short domains, alt domains) to the canonical host our
// fulfillment link-detection expects, without touching m./vm. mobile subdomains.
export function normalizeTarget(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return value;
  }
  const host = url.hostname.toLowerCase();
  if (host === 'youtu.be') {
    const id = url.pathname.replace(/^\//, '');
    const params = url.searchParams;
    params.set('v', id);
    return `https://www.youtube.com/watch?${params.toString()}`;
  }
  const alias = HOST_ALIASES[host];
  if (alias) {
    url.hostname = alias;
    return url.href;
  }
  return url.href;
}

export function validateTarget(raw, linkType, platform) {
  const value = String(raw ?? '').trim();
  if (!value) return { ok: false, value: '' };
  const hosts = PLATFORM_HOSTS[platform];
  let url = null;
  try {
    url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    url = null;
  }
  if (url && (url.username || url.password)) return { ok: false, value };
  const hostOk = Boolean(url && url.hostname.includes('.') &&
    (!hosts || hosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))));
  const hasPath = Boolean(url && url.pathname.length > 1);

  if (linkType === 'post') {
    const ok = /^https?:\/\//i.test(value) && hostOk && hasPath;
    return { ok, value: ok ? normalizeTarget(url.href) : value };
  }
  const handle = value.replace(/^@/, '');
  const handlePattern = platform === 'youtube' ? /^[A-Za-z0-9._-]{3,30}$/ : /^[A-Za-z0-9._]{2,30}$/;
  if (!isDomainLookingHandle(handle) && handlePattern.test(handle)) return { ok: true, value: handle };
  const ok = hostOk && hasPath;
  return { ok, value: ok ? normalizeTarget(url.href) : value };
}

export function sheetIdFromState(state) {
  return state && typeof state.mbaSheet === 'string' ? state.mbaSheet : null;
}

// Locale sheet search: accent-, case- and whitespace-insensitive.
export function normalizeSearch(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesLocaleQuery(haystack, query) {
  const needle = normalizeSearch(query);
  if (!needle) return true;
  const text = normalizeSearch(haystack);
  return needle.split(' ').every((part) => text.includes(part));
}

export function buildSuggestUrl(base, query) {
  const params = new URLSearchParams({
    q: query,
    'resources[type]': 'product,collection,page',
    'resources[limit]': '6',
    'resources[options][unavailable_products]': 'last',
  });
  return `${base}.json?${params}`;
}

const escapeText = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

const withThumbWidth = (url) => url + (url.includes('?') ? '&' : '?') + 'width=88';

export function renderSuggestions(results, strings, locale, currency) {
  const products = (results.products ?? []).map((product) => {
    const cents = Math.round(parseFloat(product.price) * 100);
    const image = product.image
      ? `<img src="${escapeText(withThumbWidth(product.image))}" alt="" width="44" height="44" loading="lazy">`
      : '<span class="mba-result__ph" aria-hidden="true"></span>';
    return `<li><a class="mba-result" href="${escapeText(product.url)}">${image}<span>${escapeText(product.title)}</span><small>${escapeText(formatPrice(cents, locale, currency))}</small></a></li>`;
  }).join('');
  const links = (items) => (items ?? []).map((item) => `<li><a class="mba-result" href="${escapeText(item.url)}"><span>${escapeText(item.title)}</span></a></li>`).join('');
  const collections = links(results.collections);
  const pages = links(results.pages);
  if (!products && !collections && !pages) return `<p class="mba-search__empty">${escapeText(strings.empty)}</p>`;
  const group = (label, items) => (items ? `<p class="mba-sheet__eyebrow">${escapeText(label)}</p><ul class="mba-results" role="list">${items}</ul>` : '');
  return group(strings.products, products) + group(strings.collections, collections) + group(strings.pages, pages);
}

export function pickDefaultVariant(variants) {
  return variants.find((variant) => variant.available) ?? null;
}

function initServices() {
  // Shared by click (all viewports) and hover/focus preview (desktop mega-menu only).
  const activate = (button) => {
    const container = button.closest('[data-mba-services]');
    if (!container) return null;
    const index = button.getAttribute('data-mba-platform');
    container.querySelectorAll('[data-mba-platform]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    container.querySelectorAll('[data-mba-platform-panel]').forEach((panel) => {
      panel.hidden = panel.getAttribute('data-mba-platform-panel') !== index;
    });
    return container.querySelector(`[data-mba-platform-panel="${index}"]`);
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mba-services] [data-mba-platform]');
    if (!button) return;
    const panel = activate(button);
    if (panel && window.matchMedia('(max-width: 1023px)').matches) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      panel.scrollIntoView({ block: 'nearest', behavior });
    }
  });
  // Desktop only: pointing at or tabbing to a platform previews its services, the way a
  // big-brand mega-menu does. Touch/mobile keeps the explicit two-step tap.
  const preview = (event) => {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const button = event.target?.closest?.('[data-mba-services] [data-mba-platform]');
    if (!button || button.getAttribute('aria-pressed') === 'true') return;
    activate(button);
  };
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    preview(event);
  });
  document.addEventListener('focusin', preview);
}

function initEmma() {
  const modulePromises = new WeakMap();
  // Small local guard so this mount-error fallback doesn't need to load the (otherwise
  // lazy) chat module just to reuse its safeUrl helper. Only allows http(s), mailto, or a
  // root-relative path with no control characters; anything else (javascript:, protocol-
  // relative //, backslash-confused paths, …) falls back to '#'.
  const safeHumanUrl = (value) => {
    const raw = String(value ?? '');
    if (/[\x00-\x1f\x7f]/.test(raw)) return '#';
    return /^(https?:\/\/|mailto:|\/(?![/\\]))/i.test(raw) ? raw : '#';
  };
  const renderMountError = (chat) => {
    const log = chat.querySelector('[data-mba-chat-log]');
    const strings = chat.querySelector('[data-mba-chat-strings]')?.dataset;
    if (!log || !strings) return;
    const bubble = document.createElement('div');
    bubble.className = 'mba-chat__msg mba-chat__msg--assistant';
    bubble.innerHTML = `${escapeText(strings.error)} <a class="mba-chat__human" href="${escapeText(safeHumanUrl(strings.humanUrl))}">${escapeText(strings.humanLabel)}</a>`;
    log.append(bubble);
  };
  // showChat=false returns focus to the trigger that reopens the chat (data-mba-emma-chat).
  const show = async (container, showChat) => {
    const chat = container.querySelector('[data-mba-chat]');
    const menu = container.querySelector('[data-mba-emma-menu-list]');
    chat.hidden = !showChat;
    menu.hidden = showChat;
    if (!showChat) {
      container.querySelector('[data-mba-emma-chat]')?.focus();
      return;
    }
    if (container.dataset.mbaChatReady === 'true') {
      chat.querySelector('textarea')?.focus();
      return;
    }
    try {
      let promise = modulePromises.get(container);
      if (!promise) {
        promise = import(container.dataset.module);
        modulePromises.set(container, promise);
      }
      const module = await promise;
      module.mountEmmaChat(container);
      chat.querySelector('textarea')?.focus();
    } catch {
      modulePromises.delete(container);
      renderMountError(chat);
    }
  };
  // Buttons elsewhere (e.g. homepage Emma card) open the sheet via data-mba-open="emma"
  // and jump straight to the chat with data-mba-emma-start. Delegated from document so
  // it keeps working after shopify:section:load re-renders the header.
  document.addEventListener('click', (event) => {
    const chatTrigger = event.target.closest('[data-mba-emma-chat]');
    const menuTrigger = event.target.closest('[data-mba-emma-menu]');
    const startTrigger = event.target.closest('[data-mba-emma-start]');
    if (!chatTrigger && !menuTrigger && !startTrigger) return;
    const container = event.target.closest('[data-mba-emma]') || document.querySelector('[data-mba-emma]');
    if (!container) return;
    show(container, Boolean(chatTrigger || startTrigger));
  });
  // The chat form is inert (native submit blocked) until the chat module has mounted.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-mba-chat-form]');
    if (!form) return;
    const container = form.closest('[data-mba-emma]');
    if (container?.dataset.mbaChatReady !== 'true') event.preventDefault();
  });
}

function initSearch() {
  // Elements are resolved fresh on every event (not cached at boot) so search keeps
  // working after shopify:section:load swaps the header markup for new DOM nodes.
  let timer = 0;
  let controller = null;
  document.addEventListener('mba:sheet:open', (event) => {
    if (event.target.getAttribute?.('data-mba-sheet') !== 'search') return;
    window.setTimeout(() => document.querySelector('[data-mba-search-input]')?.focus(), 60);
  });
  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-mba-search-input]');
    if (!input) return;
    const form = input.closest('[data-mba-search]');
    if (!form) return;
    const output = form.querySelector('[data-mba-search-results]');
    const strings = form.querySelector('[data-mba-search-strings]').dataset;
    window.clearTimeout(timer);
    const query = input.value.trim();
    if (query.length < 2) {
      controller?.abort();
      output.replaceChildren();
      return;
    }
    timer = window.setTimeout(async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetch(buildSuggestUrl(form.dataset.suggestUrl, query), { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        output.innerHTML = renderSuggestions(data.resources?.results ?? {}, strings, form.dataset.locale, form.dataset.currency);
      } catch (error) {
        if (error.name !== 'AbortError') output.replaceChildren();
      }
    }, 180);
  });
}

function initConfigure(api) {
  // Elements are resolved fresh on every event (not cached at boot) so the sheet keeps
  // working after shopify:section:load swaps the header markup for new DOM nodes.
  let product = null;

  const selectedVariant = (form) => {
    const checked = form.querySelector('input[name="variant"]:checked');
    return product?.variants.find((variant) => String(variant.id) === checked?.value) ?? null;
  };
  const refresh = (form) => {
    const data = form.dataset;
    const price = form.querySelector('[data-mba-configure-price]');
    const submit = form.querySelector('button[type="submit"]');
    const variant = selectedVariant(form);
    price.textContent = variant ? ` · ${formatPrice(variant.price, data.locale, data.currency)}` : '';
    submit.disabled = !variant;
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mba-configure]');
    if (!trigger) return;
    const form = document.querySelector('[data-mba-configure-form]');
    if (!form) return;
    const data = form.dataset;
    const options = form.querySelector('[data-mba-configure-options]');
    const target = form.querySelector('[data-mba-configure-target]');
    const error = form.querySelector('[data-mba-configure-error]');
    const submit = form.querySelector('button[type="submit"]');
    const title = form.querySelector('[data-mba-configure-title]');
    title.textContent = '';
    options.replaceChildren();
    error.textContent = '';
    target.value = '';
    target.removeAttribute('aria-invalid');
    submit.disabled = true;
    const source = document.querySelector(`[data-mba-product-json="${trigger.getAttribute('data-mba-configure')}"]`);
    if (!source) return;
    try {
      product = JSON.parse(source.textContent);
    } catch {
      product = null;
      error.textContent = data.addError;
      return;
    }
    title.textContent = product.title;
    const fallback = pickDefaultVariant(product.variants);
    options.replaceChildren(...product.variants.map((variant) => {
      const label = document.createElement('label');
      label.className = 'mba-option mba-option--pill';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'variant';
      radio.value = String(variant.id);
      radio.disabled = !variant.available;
      radio.checked = fallback?.id === variant.id;
      const text = document.createElement('span');
      text.textContent = variant.title;
      label.append(radio, text);
      return label;
    }));
    form.querySelector('[data-mba-configure-label]').textContent = product.linkType === 'post' ? data.postLabel : data.profileLabel;
    if (!fallback) {
      error.textContent = data.soldOut;
      return;
    }
    refresh(form);
  });

  document.addEventListener('change', (event) => {
    const options = event.target.closest('[data-mba-configure-options]');
    const form = options?.closest('[data-mba-configure-form]');
    if (form) refresh(form);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-mba-configure-form]');
    if (!form) return;
    event.preventDefault();
    const data = form.dataset;
    const target = form.querySelector('[data-mba-configure-target]');
    const error = form.querySelector('[data-mba-configure-error]');
    const submit = form.querySelector('button[type="submit"]');
    const variant = selectedVariant(form);
    if (!variant || !product) return;
    const check = validateTarget(target.value, product.linkType, product.platform);
    if (!check.ok) {
      error.textContent = product.linkType === 'post' ? data.invalidPost : data.invalidProfile;
      target.setAttribute('aria-invalid', 'true');
      target.focus();
      return;
    }
    target.removeAttribute('aria-invalid');
    error.textContent = '';
    submit.disabled = true;
    const key = product.linkType === 'post' ? data.postKey : data.profileKey;
    let added = false;
    try {
      const response = await fetch(`${data.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: { [key]: check.value } }] }),
      });
      if (!response.ok) {
        let description = '';
        try {
          const body = await response.json();
          description = body?.description || '';
        } catch {
          /* error body wasn't JSON — fall back to the generic message below */
        }
        error.textContent = description || data.addError;
        return;
      }
      added = true;
      const cart = await (await fetch(`${data.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
      document.dispatchEvent(new CustomEvent('mba:cart:updated', { detail: { itemCount: cart.item_count } }));
      api.close('configure', { restoreFocus: false });
      const drawer = document.getElementById('MinimogCartDrawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
        await drawer.onCartDrawerUpdate();
        if (typeof drawer.open === 'function') {
          drawer.open();
          document.querySelector('#MinimogCartDrawer .mb-close')?.focus();
        }
      }
    } catch {
      if (!added) error.textContent = data.addError;
    } finally {
      submit.disabled = false;
    }
  });
}

function initLocale() {
  const LOCALE = '[data-mba-locale]';

  const enhance = (scope) => {
    scope.querySelectorAll?.(LOCALE).forEach((ui) => {
      if (ui.dataset.mbaLocaleReady) return;
      ui.dataset.mbaLocaleReady = 'true';
      ui.classList.add('is-enhanced');
      ui.querySelectorAll('[data-mba-locale-search]').forEach((field) => {
        field.hidden = false;
      });
    });
  };

  const selectTab = (ui, tab) => {
    const name = tab.dataset.mbaLocaleTab;
    ui.querySelectorAll('[data-mba-locale-tab]').forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    ui.querySelectorAll('[data-mba-locale-panel]').forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.mbaLocalePanel === name);
    });
  };

  const filter = (input) => {
    const panel = input.closest('[data-mba-locale-panel]');
    if (!panel) return;
    let shown = 0;
    panel.querySelectorAll('[data-mba-locale-item]').forEach((item) => {
      const match = matchesLocaleQuery(item.dataset.search, input.value);
      item.hidden = !match;
      if (match) shown += 1;
    });
    const empty = panel.querySelector('[data-mba-locale-empty]');
    if (empty) empty.textContent = shown ? '' : empty.dataset.text || '';
  };

  enhance(document);
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));

  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.(`${LOCALE} [data-mba-locale-tab]`);
    if (!tab) return;
    selectTab(tab.closest(LOCALE), tab);
    if (window.matchMedia('(min-width: 768px) and (hover: hover)').matches) {
      document.getElementById(tab.getAttribute('aria-controls'))?.querySelector('input[type="search"]')?.focus();
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.(`${LOCALE} [data-mba-locale-tab]`);
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const ui = tab.closest(LOCALE);
      const tabs = [...ui.querySelectorAll('[data-mba-locale-tab]')];
      const index = tabs.indexOf(tab);
      let next = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      event.preventDefault();
      selectTab(ui, tabs[next]);
      tabs[next].focus();
      return;
    }
    // Enter in a search field filters; it must not submit the localization form.
    if (event.key === 'Enter' && event.target.matches?.(`${LOCALE} input[type="search"]`)) event.preventDefault();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches?.(`${LOCALE} input[type="search"]`)) filter(event.target);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches?.(`${LOCALE} input[type="radio"]`)) return;
    const value = input.closest(LOCALE).querySelector(`[data-mba-locale-value="${input.name}"]`);
    if (!value) return;
    const text = document.createElement('span');
    text.textContent = input.dataset.label || '';
    const flag = input.closest('label')?.querySelector('.mba-flag');
    const nodes = [text];
    if (flag) {
      const clone = flag.cloneNode(true);
      clone.loading = 'eager';
      nodes.unshift(clone);
    }
    value.replaceChildren(...nodes);
  });

  document.addEventListener('mba:sheet:open', (event) => {
    event.target.querySelectorAll?.(`${LOCALE} [data-mba-locale-list]`).forEach((list) => {
      const row = list.querySelector('input:checked')?.closest('label');
      if (row && !row.hidden) list.scrollTop = Math.max(0, row.offsetTop - (list.clientHeight - row.offsetHeight) / 2);
    });
  });
}

const CLUB_PROFILE_KEY = 'mba_club_profile_v1';

// The Club profile cache (sessionStorage, written by assets/mb-club.js) is the only member data the
// shell ever sees. The shell deliberately does not import mb-club.js: that module only loads on Club
// screens, and the shell runs on every page — product pages included — where it must stay silent.
export function parseClubProfile(raw) {
  try {
    const parsed = JSON.parse(String(raw ?? ''));
    const profile = parsed && typeof parsed === 'object' ? parsed.profile : null;
    return profile && typeof profile === 'object' && !Array.isArray(profile) ? profile : null;
  } catch {
    return null;
  }
}

export function firstNameOf(profile) {
  // Strings only: a poisoned cache holding a number or an object would otherwise paint a digit or a
  // bracket as the member's initial. Anything that is not a string has no initial.
  const name = profile && typeof profile === 'object' ? profile.first_name : null;
  return typeof name === 'string' ? name.trim() : '';
}

export function initialOf(profile) {
  const name = firstNameOf(profile);
  // Array spread, not name[0]: an astral-plane first character must not be cut in half.
  return name ? [...name][0].toUpperCase() : '';
}

// Same {name} placeholder as mb-club.js's formatGreeting, reimplemented here rather than imported —
// see the comment on CLUB_PROFILE_KEY. The template comes from mb_club.home.greeting via Liquid.
export function formatClubGreeting(template, name) {
  return String(template ?? '').replace('{name}', String(name ?? '').trim());
}

// The resume row links to a product. The cache is local, but a poisoned entry must not become a
// javascript: link, so only a root-relative storefront path is accepted (no //, no backslash).
export function safeClubUrl(value) {
  const raw = String(value ?? '');
  if (/[\x00-\x1f\x7f]/.test(raw)) return null;
  return /^\/(?![/\\])/.test(raw) ? raw : null;
}

export function lastServiceOf(profile) {
  const service = profile && typeof profile === 'object' ? profile.last_service : null;
  if (!service || typeof service !== 'object') return null;
  const url = safeClubUrl(service.url);
  const title = String(service.title ?? '').trim();
  return url && title ? { url, title } : null;
}

function initClubIdentity() {
  // Elements are resolved fresh on every render (not cached at boot) so the avatar, the greeting and
  // the resume row survive shopify:section:load swapping the header and the services sheet.
  let profile = null;
  try {
    profile = parseClubProfile(window.sessionStorage.getItem(CLUB_PROFILE_KEY));
  } catch {
    /* storage unavailable (private browsing): stay on the signed-out rendering */
  }

  const renderAvatar = () => {
    const initial = initialOf(profile);
    document.querySelectorAll('[data-mba-identity]').forEach((chip) => {
      const avatar = chip.querySelector('[data-mba-identity-avatar]');
      if (avatar) avatar.textContent = initial;
      if (initial) chip.dataset.mbaState = 'member';
      else delete chip.dataset.mbaState;
    });
  };

  const renderGreeting = () => {
    const name = firstNameOf(profile);
    document.querySelectorAll('[data-mba-club-greeting]').forEach((row) => {
      row.textContent = name ? formatClubGreeting(row.dataset.template, name) : '';
      row.hidden = !name;
    });
  };

  // The row is an anchor built here rather than served hidden: a link to nowhere in the served HTML
  // would be a real link for crawlers and for keyboard users before the cache is read.
  const renderResume = () => {
    const service = lastServiceOf(profile);
    document.querySelectorAll('[data-mba-club-resume]').forEach((row) => {
      row.replaceChildren();
      row.hidden = !service;
      if (!service) return;
      const link = document.createElement('a');
      link.className = 'mba-resume';
      link.href = service.url;
      const label = document.createElement('span');
      label.className = 'mba-resume__label';
      label.textContent = row.dataset.label || '';
      const title = document.createElement('span');
      title.className = 'mba-resume__title';
      title.textContent = service.title;
      link.append(label, title);
      row.append(link);
    });
  };

  const render = () => {
    renderAvatar();
    renderGreeting();
    renderResume();
  };

  render();
  document.addEventListener('shopify:section:load', render);
  document.addEventListener('mba:session:changed', (event) => {
    const detail = event.detail || {};
    profile = detail.signedIn ? (detail.profile ?? null) : null;
    render();
  });
}

// mba:modules-above

function createSheets() {
  const root = document.documentElement;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const timers = new WeakMap();
  const backdropDown = new WeakMap();
  let openId = null;
  let lastTrigger = null;
  let ignoreNextPop = false;

  if (sheetIdFromState(history.state)) {
    history.replaceState({ ...history.state, mbaSheet: undefined }, '');
  }

  function getSheet(id) {
    return document.querySelector(`dialog[${SHEET_ATTR}="${CSS.escape(id)}"]`);
  }

  function clearTimers(dialog) {
    const pending = timers.get(dialog);
    if (!pending) return;
    if (pending.raf) cancelAnimationFrame(pending.raf);
    if (pending.timeout) window.clearTimeout(pending.timeout);
    timers.delete(dialog);
  }

  function openCart() {
    const drawer = document.getElementById('MinimogCartDrawer');
    if (drawer && typeof drawer.open === 'function') {
      if (openId) close(openId);
      drawer.open();
      return true;
    }
    return false;
  }

  function open(id, trigger) {
    if (id === 'cart') return openCart();
    const dialog = getSheet(id);
    if (!dialog) return false;
    if (openId === id) return true;
    const currentDialog = openId ? getSheet(openId) : null;
    const candidateTrigger = trigger || document.activeElement;
    if (openId) close(openId, { fromHistory: true });
    if (!(currentDialog && candidateTrigger && currentDialog.contains(candidateTrigger))) {
      lastTrigger = candidateTrigger;
    }
    clearTimers(dialog);
    const bar = document.querySelector('[data-mba-appbar]');
    if (bar) root.style.setProperty('--mba-sheet-top', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
    if (!dialog.open) dialog.showModal();
    const raf = requestAnimationFrame(() => dialog.classList.add('is-open'));
    timers.set(dialog, { raf, timeout: null });
    root.classList.add('mba-lock');
    openId = id;
    if (sheetIdFromState(history.state)) {
      history.replaceState({ ...(history.state || {}), mbaSheet: id }, '');
    } else {
      history.pushState({ ...(history.state || {}), mbaSheet: id }, '');
    }
    dialog.dispatchEvent(new CustomEvent('mba:sheet:open', { bubbles: true }));
    return true;
  }

  function close(id = openId, { fromHistory = false, restoreFocus = true } = {}) {
    const dialog = getSheet(id);
    if (!dialog || !dialog.open) return;
    clearTimers(dialog);
    const panel = dialog.querySelector('.mba-sheet__panel');
    dialog.classList.remove('is-open');
    if (panel) panel.style.transform = '';
    if (openId === id) openId = null;
    const trigger = lastTrigger;
    const finish = () => {
      if (!dialog.classList.contains('is-open')) dialog.close();
      if (!openId) root.classList.remove('mba-lock');
      if (restoreFocus && trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    };
    if (reduceMotion()) finish();
    else timers.set(dialog, { raf: null, timeout: window.setTimeout(finish, 220) });
    if (!fromHistory && sheetIdFromState(history.state) === id) {
      ignoreNextPop = true;
      history.back();
    }
  }

  function bindSwipe(dialog) {
    const panel = dialog.querySelector('.mba-sheet__panel');
    const handle = dialog.querySelector('[data-mba-drag]');
    if (!panel || !handle) return;
    let startY = 0;
    let startTime = 0;
    let delta = 0;
    let dragging = false;
    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      if (window.matchMedia('(min-width: 1024px)').matches) return;
      dragging = true;
      startY = event.clientY;
      startTime = event.timeStamp;
      delta = 0;
      handle.setPointerCapture(event.pointerId);
      panel.style.transition = 'none';
    });
    handle.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      delta = Math.max(0, event.clientY - startY);
      panel.style.transform = `translateY(${delta}px)`;
    });
    handle.addEventListener('pointerup', (event) => {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = '';
      const velocity = delta / Math.max(1, event.timeStamp - startTime);
      panel.style.transform = '';
      if (delta > panel.offsetHeight * 0.3 || velocity > 0.8) close(dialog.getAttribute(SHEET_ATTR));
    });
    handle.addEventListener('pointercancel', () => {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = '';
      panel.style.transform = '';
    });
  }

  function bindAll() {
    document.querySelectorAll(`dialog[${SHEET_ATTR}]`).forEach((dialog) => {
      if (dialog.dataset.mbaBound) return;
      dialog.dataset.mbaBound = 'true';
      const id = dialog.getAttribute(SHEET_ATTR);
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close(id);
      });
      dialog.addEventListener('close', () => {
        if (dialog.open) return;
        dialog.classList.remove('is-open');
        if (openId === id) {
          openId = null;
          root.classList.remove('mba-lock');
          if (sheetIdFromState(history.state) === id) {
            ignoreNextPop = true;
            history.back();
          }
        }
      });
      dialog.addEventListener('pointerdown', (event) => {
        backdropDown.set(dialog, event.target === dialog);
      });
      bindSwipe(dialog);
    });
  }

  bindAll();
  document.addEventListener('shopify:section:load', bindAll);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mba-open]');
    if (trigger) {
      if (open(trigger.getAttribute('data-mba-open'), trigger)) event.preventDefault();
      return;
    }
    if (event.target.closest('[data-mba-close]')) {
      event.preventDefault();
      close(event.target.closest('dialog[data-mba-sheet]')?.getAttribute(SHEET_ATTR) ?? openId);
      return;
    }
    if (openId && !event.defaultPrevented) {
      const link = event.target.closest('a[href]');
      const openDialog = getSheet(openId);
      if (link && openDialog && openDialog.contains(link) && !link.closest('[data-mba-open]') && !link.hasAttribute('data-mba-native')) {
        const samePageHash = link.href.split('#')[0] === location.href.split('#')[0] && link.href.includes('#');
        if (samePageHash) {
          event.preventDefault();
          close(openId, { fromHistory: true });
          history.replaceState({ ...(history.state || {}), mbaSheet: undefined }, '', link.href);
          document.getElementById(decodeURIComponent(link.hash.slice(1)))?.scrollIntoView();
          return;
        }
        const unmodified = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
        const sameOrigin = link.origin === window.location.origin;
        const blocked = link.target === '_blank' || link.hasAttribute('download');
        if (unmodified && sameOrigin && !blocked) {
          event.preventDefault();
          location.replace(link.href);
          return;
        }
      }
    }
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute(SHEET_ATTR) && backdropDown.get(event.target)) {
      close(event.target.getAttribute(SHEET_ATTR));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ' || event.repeat) return;
    const trigger = event.target.closest && event.target.closest('a[data-mba-open]');
    if (!trigger) return;
    event.preventDefault();
    const id = trigger.getAttribute('data-mba-open');
    if (!open(id, trigger)) trigger.click();
  });

  window.addEventListener('popstate', () => {
    if (ignoreNextPop) {
      ignoreNextPop = false;
      return;
    }
    if (openId) close(openId, { fromHistory: true });
  });

  return { open, close };
}

function initAppbar() {
  const appbar = document.querySelector('[data-mba-appbar]');
  if (!appbar) return;
  let lastY = window.scrollY;
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      appbar.classList.toggle('is-compact', y > 64 && y >= lastY);
      lastY = y;
      ticking = false;
    });
  }, { passive: true });
}

function setCartCount(count) {
  document.querySelectorAll('[data-mba-cart-count]').forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });
}

function initCartCount() {
  const drawer = document.getElementById('MinimogCartDrawer');
  if (drawer) {
    new MutationObserver(() => setCartCount(parseInt(drawer.dataset.cartCount, 10) || 0))
      .observe(drawer, { attributes: true, attributeFilter: ['data-cart-count'] });
  }
  document.addEventListener('mba:cart:updated', (event) => {
    const detail = event.detail || {};
    const count = Number(detail.itemCount ?? detail.item_count);
    if (!Number.isFinite(count)) return;
    setCartCount(count);
  });
}

/* Signalement du clic : purement statistique. L'argent, lui, passe par l'attribut de panier
   ci-dessous. Un échec réseau ici ne doit jamais empêcher la page de vivre. */
function reportClick(root) {
  const endpoint = root?.dataset?.mbaClickEndpoint;
  if (!endpoint || endpoint.slice(0, 8) !== 'https://') return;
  const payload = clickPayload(window.location.href, window.location.search);
  if (!payload) return;
  try {
    if (window.sessionStorage.getItem(REF_CLICK_KEY) === payload.ref_code) return;
  } catch {
    /* stockage indisponible : on signale, quitte à compter deux fois */
  }
  const headers = { 'Content-Type': 'application/json' };
  const key = root.dataset.mbaClickKey;
  if (key) {
    headers.apikey = key;
    headers.Authorization = `Bearer ${key}`;
  }
  try {
    fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
  } catch {
    /* ignore */
  }
  try {
    window.sessionStorage.setItem(REF_CLICK_KEY, payload.ref_code);
  } catch {
    /* ignore */
  }
}

async function syncReferral() {
  const fromUrl = refFromSearch(window.location.search);
  if (fromUrl) {
    document.cookie = `${REF_COOKIE}=${fromUrl}; path=/; max-age=${30 * 24 * 3600}; SameSite=Lax; Secure`;
  }
  const ref = fromUrl || normalizeRef(readCookie(document.cookie, REF_COOKIE));
  if (!ref) return;
  const before = refSyncKey(ref, readCookie(document.cookie, 'cart'));
  try {
    if (before && window.sessionStorage.getItem(REF_SYNC_KEY) === before) return;
  } catch {
    /* storage unavailable: sync every page, like the legacy script */
  }
  const rootUrl = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${rootUrl}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { mb_ref: ref } }),
  });
  if (!response.ok) return;
  const after = refSyncKey(ref, readCookie(document.cookie, 'cart'));
  try {
    if (after) window.sessionStorage.setItem(REF_SYNC_KEY, after);
  } catch {
    /* ignore */
  }
}

function boot() {
  const api = createSheets();
  initAppbar();
  initCartCount();
  reportClick(document.querySelector('[data-mba-appbar]'));
  syncReferral().catch(() => {});
  window.MyBoostShell = api;
  initServices();
  initEmma();
  initSearch();
  initConfigure(api);
  initLocale();
  initClubIdentity();
  // mba:init-modules
}

if (typeof document !== 'undefined' && !window.__mbaShell) {
  window.__mbaShell = true;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
